import { css } from "@emotion/css";
import { mergeProps } from "@react-aria/utils";
import {
  Column as AriaColumn,
  ColumnProps,
  ColumnResizer,
} from "react-aria-components";

import { textAlignment } from "./Cell";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { getTextStyle } from "../../../foundations/typography/styles";

interface Props extends ColumnProps {
  textAlignment?: textAlignment;
  displayRightBorder?: boolean;
  allowsResizing?: boolean;
}

export function Column(props: Props) {
  const { allowsResizing, ...columnProps } = props;
  const styles = getStyles({
    textAlignment: props.textAlignment || "left",
    displayRightBorder: props.displayRightBorder,
    allowsResizing,
  });
  return (
    <AriaColumn
      {...mergeProps(columnProps, {
        className: css(
          styles.column,
          props.width && { width: props.width },
          props.allowsSorting && styles.sortable
        ),
      })}
    >
      {(columnRenderProps) => {
        const { allowsSorting, sortDirection } = columnRenderProps;

        return (
          <>
            <div className={styles.button}>
              {typeof props.children === "function"
                ? props.children(columnRenderProps)
                : props.children}

              {allowsSorting && sortDirection === "ascending" && (
                <Icon aria-hidden="true" type="mini" name="arrow_upward" />
              )}
              {allowsSorting && sortDirection === "descending" && (
                <Icon aria-hidden="true" type="mini" name="arrow_downward" />
              )}
              {allowsSorting && sortDirection === undefined && (
                <Icon aria-hidden="true" type="mini" name="swap_vert" />
              )}
            </div>
            {allowsResizing && <ColumnResizer className={styles.resizer} />}
          </>
        );
      }}
    </AriaColumn>
  );
}

function getStyles({
  textAlignment,
  displayRightBorder,
  allowsResizing,
}: {
  textAlignment: textAlignment;
  displayRightBorder?: boolean;
  allowsResizing?: boolean;
}) {
  return {
    column: css(
      {
        outline: "none",
        position: allowsResizing ? "relative" : undefined,
        paddingBottom: `calc(var(--space-2-5) - 2px)`,
        paddingLeft: "var(--space-3)",
        paddingRight: allowsResizing ? "var(--space-3)" : "var(--space-05)",
        paddingTop: "var(--space-2-5)",
        textAlign: textAlignment,
        "&[data-focus-visible]": {
          boxShadow: "var(--focus-action)",
        },
        borderRight: displayRightBorder
          ? "1px solid var(--border-base)"
          : undefined,
      },
      getTextStyle("global/button-medium")
    ),
    resizer: css({
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      width: "var(--space-2)",
      touchAction: "none",
      cursor: "col-resize",
      "&[data-hovered], &[data-resizing], &[data-focus-visible]": {
        backgroundColor: "var(--border-strong)",
      },
    }),
    button: css({
      alignItems: "center",
      borderRadius: "var(--border-radius-2)",
      display: "inline-flex",
      gap: "var(--space-1)",
      paddingBottom: "var(--space-1)",
      paddingLeft: "var(--space-05)",
      paddingRight: "var(--space-05)",
      paddingTop: "var(--space-1)",
    }),
    sortable: css({
      cursor: "pointer",
      "&[data-focus-visible]": {
        boxShadow: "none",
        "> div:first-child": {
          boxShadow: "var(--focus-action)",
        },
      },
      "&[data-hovered] > div:first-child": {
        backgroundColor: "var(--background-ghost-hover)",
      },
    }),
  };
}
